import React, { useEffect, useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import dayjs from "dayjs";
import {
  StyledEngineProvider,
} from "@mui/material";
import { SingleShow } from "../../../../../models/Show";
import { readSessionsAudience } from "../../../../../services/SessionService";
import { SessionInterface, Sessions, SingleSession } from "../../../../../models/Session";
import { isLoggedIn } from "../../../../../utils/auth";
import { UserInterface } from "../../../../../models/User";
import PromoCodeErrorDialog from "./BookingInterestErrorDialog/BookingInterestErrorDialog";
import { ShowHeroDetails, ImageBox, SessionSelection, InfoPanel, TitleSubtitleList, SessionSelectionWidget, Modal } from 'src/components';
import { PageContainer, GlobalContainer, MaxWidthContainer, ListContainer } from "./SessionStep.styles";
import { Modals, useModal } from "src/contexts/ModalContext";
import { usePriorityAllocationStore } from "src/store/usePriorityAllocationStore";
interface SessionStepProps extends WithRouterProps {
  show: SingleShow;
  promoCode: string | undefined;
  user: UserInterface | null | undefined;
  onNextButtonClick: (sessionId: number, continueWithNonPriority?: boolean) => void;
}

const SessionStep: React.FC<SessionStepProps> = ({
  router,
  show,
  promoCode,
  user,
  onNextButtonClick
}) => {
  const [sessions, setSessions] = useState<Sessions>(null);
  const [selectedSession, setSeletedSession] = useState<SingleSession>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<number | undefined>(undefined);
  const [promoCodeError, setPromoCodeError] = useState<boolean>(false);
  const [promoCodeErrorDialogOpen, setPromoCodeErrorDialogOpen] = useState<boolean>(false);
  const [promoCodeErrorDialogTitle, setPromoCodeErrorDialogTitle] = useState<string>("");
  const [promoCodeErrorDialogBody, setPromoCodeErrorDialogBody] = useState<string>("");
  const [promoCodeErrorDialogAcceptButtonText, setPromoCodeErrorDialogAcceptButtonText] = useState<string>("");
  const [promoCodeErrorDialogCancelButtonText, setPromoCodeErrorDialogCancelButtonText] = useState<string>("");
  const [handlePromoCodeErrorDialogCancelButtonClick, setPromoCodeErrorDialogHandleCancelButtonClick] = useState<Function>();
  const [handlePromoCodeErrorDialogAcceptButtonClick, setPromoCodeErrorDialogHandleAcceptButtonClick] = useState<Function>(() => () => setPromoCodeErrorDialogOpen(false));

  const { openModal, closeModal, modalState } = useModal();

  const priorityAllocFullSessions = usePriorityAllocationStore((state) => state.priorityAllocFullSessions);

  useEffect(() => {
    if (show) {
      if (show.sessionCount != 0) {
        readAllSessions(true);
      } else {
        setSessions([]);
      }
    }
  }, [show]);

  const checkPromoCode = (currentSessions: SessionInterface[]) => {
    if (!promoCode) return;
    if (!currentSessions.some(s => s.isPriorityActive == true)) {
      openPromoCodeErrorDialogAfterPageLoading();
      setPromoCodeErrorDialogTitle("Priority Registration deadline expired")
      setPromoCodeErrorDialogBody("We regret to inform you that the deadline for priority ticket registration has expired. However, regular ticket registration is still available. \n \nWe apologize for any inconvenience caused and appreciate your understanding. Thank you for your continued support!")
    }
    else if (!currentSessions.some(s => s.isPriorityActive == true && s.isPrioritySeatAvailable == true)) {
      setPromoCodeErrorDialogTitle("Priority Tickets Fully Booked :(")
      setPromoCodeErrorDialogBody("We regret to inform you that all priority tickets have been fully booked. However, regular ticket registration is still available. \n \nWe apologize for any inconvenience caused and appreciate your understanding. Thank you for your continued support!")

      openPromoCodeErrorDialogAfterPageLoading();
    }
  }

  const openPromoCodeErrorDialogAfterPageLoading = () => {
    setPromoCodeErrorDialogAcceptButtonText("Check Available Sessions")
    setPromoCodeErrorDialogCancelButtonText("")
    setPromoCodeErrorDialogOpen(true)
    setPromoCodeErrorDialogHandleCancelButtonClick(undefined)
    setPromoCodeErrorDialogHandleAcceptButtonClick(() => () => setPromoCodeErrorDialogOpen(false))
    setPromoCodeError(true)
  }

  const openPromoCodeErrorDialogOnSessionCardClick = (session: SessionInterface) => {
    setPromoCodeErrorDialogTitle("Session not eligible for priority promotion")
    setPromoCodeErrorDialogBody("The session you have selected is not part of the priority promotion. While you can proceed with regular ticket booking, please note that ticket availability is not guaranteed for this session.")
    setPromoCodeErrorDialogAcceptButtonText("Proceed with regular booking")
    setPromoCodeErrorDialogCancelButtonText("View Eligible Sessions")
    setPromoCodeErrorDialogOpen(true)
    setPromoCodeErrorDialogHandleCancelButtonClick(() => () => setPromoCodeErrorDialogOpen(false))
    setPromoCodeErrorDialogHandleAcceptButtonClick(() => () => {
      handleBookingInterestRegistration(session)
    })
  }

  const onSessionCardClickHandler = (session: SessionInterface) => {
    if (promoCode && !promoCodeError && (!session.isPriorityActive || !session.isPrioritySeatAvailable)) {
      openPromoCodeErrorDialogOnSessionCardClick(session);
    } else {
      handleBookingInterestRegistration(session)
    }
  };

  const handleBookingInterestRegistration = (session: SessionInterface) => {
    setSeletedSession(session);
    setSelectedSessionId(session?.id);
    if (selectedSessionId === session?.id) {
      //resets the selected session to null if you toggle
      setSeletedSession(null);
      setSelectedSessionId(undefined);
    }
  }

  const readAllSessions = (onInit: boolean) => {
    setSessions(null);
    readSessionsAudience(
      show?.id?.toString(),
      '1000',
      promoCode,
      new Date().toJSON(),
    ).then((sessionResponse) => {

      if (sessionResponse.data) {
        const currentSessions = sessionResponse.data.sort((a, b) => dayjs(a.sortIndex).diff(dayjs(b.sortIndex)));

        const activeSessionDates = currentSessions.map(
          (session) => new Date(session.arrivalTime!)
        );
        activeSessionDates.forEach((session) => session.setHours(0, 0, 0, 0));

        setSessions(currentSessions);
        checkPromoCode(currentSessions);
      }
    });
  };

  const handleApplyForTicketsAndNextStep = async () => {
    if (selectedSession?.id) {
      if (priorityAllocFullSessions[selectedSession.id]?.isFull) {
        openModal(Modals.NonPrioritySessionSelected, (successful?: boolean) => {
          if (successful) {
            closeModal();
            setSeletedSession(null);
            setSelectedSessionId(undefined);
          } else {
            closeModal();
            onNextButtonClick(selectedSession?.id!, true);
          }
        });
        return;
      }
      onNextButtonClick(selectedSession?.id);
    }
  };

  const isPriority = Object.values(priorityAllocFullSessions).some(value => value.isFull === false);

  return (
    <StyledEngineProvider injectFirst>
      <GlobalContainer>
        <ImageBox
          src={show?.image?.data ? `data:image/png;base64,${show?.image?.data}` : ""}
          alt={`${show?.name} Image`}
          variant="header"
        />
        <PageContainer>
          <MaxWidthContainer>
            <ShowHeroDetails isPriority={isPriority} showTitle={show?.name} showSynopsis={show?.synopsis} />
            <SessionSelection
              showName={show?.name}
              showMinimumAge={show?.defaultMinimumAgeRequirement}
              sessions={sessions}
              onSessionCardClickHandler={onSessionCardClickHandler}
              selectedSessionId={selectedSessionId}
            />

            <InfoPanel title="How Do I Get Tickets?" id="#more-info">
              <ListContainer>
                <TitleSubtitleList
                  number={1}
                  title="Join us!"
                  subTitle={
                    <>
                      There is no catch – membership is free, tickets are free and we will never sell your data. <br />
                      Be the first to know when new opportunities arise and the more shows you attend, the better the perks!
                    </>
                  }
                />
                <TitleSubtitleList
                  number={2}
                  title="Register Your Interest!"
                  subTitle={
                    <>
                      Select the session you would like to attend and the number of tickets you need. <br />
                      Tell us the name and age of your guests and if they have any accessibility requirements. <br />
                      You can change their details later if you need to. <br />
                      You’ve not got tickets yet but you are one step closer!
                    </>
                  }
                />
                <TitleSubtitleList
                  number={3}
                  title="Hold tight!"
                  subTitle="If your session is sending out ticket offers you may be able to accept a ticket offer straight away. In all other cases keep an eagle eye on your email inbox (don’t forget to check your spam folder too!). You can always login to your account and check the status of your ticket request as well."
                />
                <TitleSubtitleList
                  number={4}
                  title="Accept your ticket offer!"
                  subTitle="If you receive a ticket offer you will only have 48 hours to accept!"
                />
              </ListContainer>
            </InfoPanel>
          </MaxWidthContainer>

          <SessionSelectionWidget
            showName={show?.name}
            selectedSession={selectedSession}
            setSeletedSession={setSeletedSession}
            setSelectedSessionId={setSelectedSessionId}
            onNextButtonClick={async () => await handleApplyForTicketsAndNextStep()}
            user={user}
          />

          <PromoCodeErrorDialog
            open={promoCodeErrorDialogOpen}
            setOpen={setPromoCodeErrorDialogOpen}
            dialogTitle={promoCodeErrorDialogTitle}
            dialogBody={promoCodeErrorDialogBody}
            dialogAcceptButtonText={promoCodeErrorDialogAcceptButtonText}
            dialogCancelButtonText={promoCodeErrorDialogCancelButtonText}
            handleCancelButtonClick={handlePromoCodeErrorDialogCancelButtonClick}
            handleAcceptButtonClick={handlePromoCodeErrorDialogAcceptButtonClick}
          />
        </PageContainer>

        <Modal />

      </GlobalContainer>
    </StyledEngineProvider>
  );
};

export default withRouter(SessionStep);
